import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import PremiumPurchaseModal from './PremiumPurchaseModal';

const PremiumLockComponent = ({
    title,
    message,
    onUpgrade,
    paymentModuleKey,
    paymentModuleTitle,
    primaryLabel = "Premium'u Keşfet",
    primaryDisabled = false
}) => {
    const [purchaseOpen, setPurchaseOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!paymentModuleKey) return;
        if (!location.state?.openPremiumPurchase) return;
        const stateModule = location.state?.paymentModuleKey;
        if (stateModule && stateModule !== paymentModuleKey) return;
        setPurchaseOpen(true);
    }, [location.state, paymentModuleKey]);

    const handlePrimaryAction = () => {
        if (primaryDisabled) return;
        if (paymentModuleKey) {
            setPurchaseOpen(true);
            return;
        }
        if (typeof onUpgrade === 'function') onUpgrade();
    };

    return (
        <>
            <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-slate-50 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 min-h-[500px] flex items-center justify-center group/lock">
                <div className="absolute inset-0 pointer-events-none select-none p-12 opacity-[0.03] dark:opacity-[0.05]">
                    <div className="grid h-full grid-cols-7 gap-6">
                        {[...Array(7)].map((_, i) => (
                            <div key={i} className="flex flex-col gap-4">
                                <div className="h-10 w-full rounded-2xl bg-slate-400" />
                                <div className="flex-1 w-full rounded-[2rem] bg-slate-400" />
                                <div className="flex-1 w-full rounded-[2rem] bg-slate-400" />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-indigo-500/5 pointer-events-none" />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="relative z-10 max-w-lg px-8 text-center"
                >
                    <motion.div
                        animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                        className="relative mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[2rem] border-4 border-white bg-gradient-to-br from-yellow-300 to-orange-500 shadow-2xl shadow-orange-500/40 dark:border-slate-800"
                    >
                        <span className="material-symbols-outlined text-[48px] font-black text-white drop-shadow-md">workspace_premium</span>
                        <div className="absolute inset-0 rounded-[2rem] bg-white opacity-20 animate-ping" />
                    </motion.div>

                    <h2 className="mb-6 font-display text-4xl font-black leading-none tracking-tight text-slate-800 dark:text-white">
                        {title || 'Premium Mutfak'}
                    </h2>

                    <p className="mb-10 text-lg font-medium leading-relaxed text-slate-500 dark:text-slate-400">
                        {message || 'Premium mutfak deneyimini acmak icin fiyat planlarini inceleyin ve EFT ile odemenizi tamamlayin.'}
                    </p>

                    <div className="flex flex-col items-center gap-6">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handlePrimaryAction}
                            disabled={primaryDisabled}
                            className={`group flex w-full items-center justify-center gap-3 rounded-[2rem] px-10 py-5 text-xl font-black shadow-2xl transition-all ${
                                primaryDisabled
                                    ? 'cursor-not-allowed bg-slate-300 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                    : 'bg-slate-900 text-white hover:shadow-primary/20 dark:bg-white dark:text-slate-900'
                            }`}
                        >
                            <span className="material-symbols-outlined font-bold transition-transform group-hover:rotate-12">bolt</span>
                            {primaryLabel}
                        </motion.button>

                        <div className="flex items-center gap-6 rounded-full bg-slate-50 px-6 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:bg-slate-800/50">
                            <span className="flex items-center gap-1.5 underline decoration-primary/30 decoration-2 underline-offset-4">Ozel Tarifler</span>
                            <span className="h-1 w-1 rounded-full bg-slate-300" />
                            <span className="flex items-center gap-1.5 underline decoration-indigo-500/30 decoration-2 underline-offset-4">Akilli Planlar</span>
                        </div>
                    </div>
                </motion.div>

                <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-[100px] transition-all duration-1000 group-hover/lock:bg-primary/10" />
            </div>

            {paymentModuleKey ? (
                <PremiumPurchaseModal
                    isOpen={purchaseOpen}
                    onClose={(reason) => {
                        if (reason === 'open') {
                            setPurchaseOpen(true);
                            return;
                        }
                        if (location.state?.openPremiumPurchase) {
                            navigate(location.pathname, { replace: true, state: {} });
                        }
                        setPurchaseOpen(false);
                    }}
                    preferredModuleKey={paymentModuleKey}
                    preferredModuleTitle={paymentModuleTitle || title}
                    initialIntentId={location.state?.paymentIntentId || null}
                />
            ) : null}
        </>
    );
};

export default PremiumLockComponent;
